// src/hooks/usePropertySceneEditor.ts
/**
 * Property Scene Editor Hook
 * Manages state for scene editing in Property Video Generator Step 5
 */

import { useReducer, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IPropertySceneEditorState,
  PropertySceneEditorAction,
  IUsePropertySceneEditorReturn,
  PropertySceneLayout,
  IBulkProgress,
  IPropertySceneExtended,
} from '@/@types/propertySceneEditor';
import type { PromptTemplateId } from '@/@types/promptTemplates';

// ============================================================================
// Constants
// ============================================================================

const LAYOUT_STORAGE_KEY = 'property-scene-editor-layout';

const initialBulkProgress: IBulkProgress = {
  status: 'idle',
  total: 0,
  completed: 0,
  failed: 0,
};

// ============================================================================
// Initial State
// ============================================================================

const getInitialState = (): IPropertySceneEditorState => ({
  layout: (localStorage.getItem(LAYOUT_STORAGE_KEY) as PropertySceneLayout) || 'horizontal',
  editingSceneId: null,
  imageSelectionModalOpen: false,
  imageSelectionSceneId: null,
  promptModalOpen: false,
  promptModalSceneId: null,
  voiceoverProgress: initialBulkProgress,
  videoProgress: initialBulkProgress,
  savingSceneIds: new Set(),
  regeneratingVoiceoverIds: new Set(),
  regeneratingVideoIds: new Set(),
  deletingSceneIds: new Set(),
  isPlanningScenes: false,
  hasTriggeredPlanning: false,
  error: null,
});

// ============================================================================
// Reducer
// ============================================================================

function reducer(
  state: IPropertySceneEditorState,
  action: PropertySceneEditorAction
): IPropertySceneEditorState {
  switch (action.type) {
    case 'SET_LAYOUT':
      localStorage.setItem(LAYOUT_STORAGE_KEY, action.payload);
      return { ...state, layout: action.payload };

    case 'SET_EDITING_SCENE':
      return { ...state, editingSceneId: action.payload };

    case 'OPEN_IMAGE_MODAL':
      return {
        ...state,
        imageSelectionModalOpen: true,
        imageSelectionSceneId: action.payload,
      };

    case 'CLOSE_IMAGE_MODAL':
      return {
        ...state,
        imageSelectionModalOpen: false,
        imageSelectionSceneId: null,
      };

    case 'OPEN_PROMPT_MODAL':
      return {
        ...state,
        promptModalOpen: true,
        promptModalSceneId: action.payload,
      };

    case 'CLOSE_PROMPT_MODAL':
      return {
        ...state,
        promptModalOpen: false,
        promptModalSceneId: null,
      };

    case 'START_PLANNING':
      return { ...state, isPlanningScenes: true, error: null };

    case 'PLANNING_COMPLETE':
      return { ...state, isPlanningScenes: false };

    case 'PLANNING_ERROR':
      return { ...state, isPlanningScenes: false, error: action.payload };

    case 'SET_TRIGGERED_PLANNING':
      return { ...state, hasTriggeredPlanning: true };

    case 'START_SAVE': {
      const newSavingIds = new Set(state.savingSceneIds);
      newSavingIds.add(action.payload);
      return { ...state, savingSceneIds: newSavingIds };
    }

    case 'SAVE_COMPLETE': {
      const newSavingIds = new Set(state.savingSceneIds);
      newSavingIds.delete(action.payload);
      return { ...state, savingSceneIds: newSavingIds };
    }

    case 'START_REGENERATE_VOICEOVER': {
      const newIds = new Set(state.regeneratingVoiceoverIds);
      newIds.add(action.payload);
      return { ...state, regeneratingVoiceoverIds: newIds };
    }

    case 'REGENERATE_VOICEOVER_COMPLETE': {
      const newIds = new Set(state.regeneratingVoiceoverIds);
      newIds.delete(action.payload);
      return { ...state, regeneratingVoiceoverIds: newIds };
    }

    case 'START_REGENERATE_VIDEO': {
      const newIds = new Set(state.regeneratingVideoIds);
      newIds.add(action.payload);
      return { ...state, regeneratingVideoIds: newIds };
    }

    case 'REGENERATE_VIDEO_COMPLETE': {
      const newIds = new Set(state.regeneratingVideoIds);
      newIds.delete(action.payload);
      return { ...state, regeneratingVideoIds: newIds };
    }

    case 'START_BULK_REGENERATE_VOICEOVERS': {
      const newIds = new Set(state.regeneratingVoiceoverIds);
      action.payload.forEach((id) => newIds.add(id));
      return { ...state, regeneratingVoiceoverIds: newIds };
    }

    case 'CLEAR_BULK_REGENERATING_VOICEOVERS': {
      return { ...state, regeneratingVoiceoverIds: new Set() };
    }

    case 'START_BULK_REGENERATE_VIDEOS': {
      const newIds = new Set(state.regeneratingVideoIds);
      action.payload.forEach((id) => newIds.add(id));
      return { ...state, regeneratingVideoIds: newIds };
    }

    case 'CLEAR_BULK_REGENERATING_VIDEOS': {
      return { ...state, regeneratingVideoIds: new Set() };
    }

    case 'START_DELETE': {
      const newIds = new Set(state.deletingSceneIds);
      newIds.add(action.payload);
      return { ...state, deletingSceneIds: newIds };
    }

    case 'DELETE_COMPLETE': {
      const newIds = new Set(state.deletingSceneIds);
      newIds.delete(action.payload);
      return { ...state, deletingSceneIds: newIds };
    }

    case 'START_BULK_VOICEOVER':
      return {
        ...state,
        voiceoverProgress: { ...initialBulkProgress, status: 'generating' },
      };

    case 'BULK_VOICEOVER_PROGRESS':
      return { ...state, voiceoverProgress: action.payload };

    case 'START_BULK_VIDEO':
      return {
        ...state,
        videoProgress: { ...initialBulkProgress, status: 'generating' },
      };

    case 'BULK_VIDEO_PROGRESS':
      return { ...state, videoProgress: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'RESET':
      return getInitialState();

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function usePropertySceneEditor(
  videoId: Id<'propertyVideos'> | null,
  voiceId?: string
): IUsePropertySceneEditorReturn {
  const [state, dispatch] = useReducer(reducer, undefined, getInitialState);

  // Queries - Note: getPropertyScenes expects 'propertyVideoId' argument
  const scenes = useQuery(
    api.property.queries.getPropertyScenes,
    videoId ? { propertyVideoId: videoId } : 'skip'
  ) as IPropertySceneExtended[] | undefined;

  // Mutations
  const updateVoiceoverTextMutation = useMutation(api.property.mutations.updateSceneVoiceoverText);
  const updateImagePromptMutation = useMutation(api.property.mutations.updateSceneImagePrompt);
  const updateSelectedImageMutation = useMutation(api.property.mutations.updateSceneSelectedImage);
  const updateSceneTemplateMutation = useMutation(api.property.mutations.updateSceneTemplate);
  const updateSceneNegativePromptMutation = useMutation(api.property.mutations.updateSceneNegativePrompt);
  const reorderScenesMutation = useMutation(api.property.mutations.reorderPropertyScenes);
  const deleteSceneMutation = useMutation(api.property.mutations.deletePropertyScenePublic);

  // Actions for scene operations
  const regenerateVoiceoverAction = useAction(api.property.videoGeneration.regenerateSceneVoiceover);
  const regenerateVideoAction = useAction(api.property.videoGeneration.regenerateSceneVideo);
  const generateAllVoiceoversAction = useAction(api.property.videoGeneration.generateAllVoiceovers);
  const regenerateAllVoiceoversAction = useAction(api.property.videoGeneration.regenerateAllVoiceovers);
  const generateAllVideosAction = useAction(api.property.videoGeneration.generateAllVideos);

  // Action for scene planning
  const planVideoScenesAction = useAction(api.property.sceneGeneration.planVideoScenes);

  // Layout management
  const setLayout = useCallback((layout: PropertySceneLayout) => {
    dispatch({ type: 'SET_LAYOUT', payload: layout });
  }, []);

  // Modal management
  const openImageSelectionModal = useCallback((sceneId: Id<'propertyScenes'>) => {
    dispatch({ type: 'OPEN_IMAGE_MODAL', payload: sceneId });
  }, []);

  const closeImageSelectionModal = useCallback(() => {
    dispatch({ type: 'CLOSE_IMAGE_MODAL' });
  }, []);

  const openPromptModal = useCallback((sceneId: Id<'propertyScenes'>) => {
    dispatch({ type: 'OPEN_PROMPT_MODAL', payload: sceneId });
  }, []);

  const closePromptModal = useCallback(() => {
    dispatch({ type: 'CLOSE_PROMPT_MODAL' });
  }, []);

  // Scene editing actions
  const updateVoiceoverText = useCallback(
    async (sceneId: Id<'propertyScenes'>, text: string) => {
      dispatch({ type: 'START_SAVE', payload: sceneId });
      try {
        await updateVoiceoverTextMutation({ sceneId, editedVoiceoverText: text });
        toast.success('Voiceover text saved');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save';
        toast.error(message);
      } finally {
        dispatch({ type: 'SAVE_COMPLETE', payload: sceneId });
      }
    },
    [updateVoiceoverTextMutation]
  );

  const updateImagePrompt = useCallback(
    async (sceneId: Id<'propertyScenes'>, prompt: string) => {
      dispatch({ type: 'START_SAVE', payload: sceneId });
      try {
        await updateImagePromptMutation({ sceneId, editedImagePrompt: prompt });
        toast.success('Prompt saved');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save';
        toast.error(message);
      } finally {
        dispatch({ type: 'SAVE_COMPLETE', payload: sceneId });
      }
    },
    [updateImagePromptMutation]
  );

  const updateSelectedImage = useCallback(
    async (sceneId: Id<'propertyScenes'>, imageId: Id<'_storage'>) => {
      dispatch({ type: 'START_SAVE', payload: sceneId });
      try {
        await updateSelectedImageMutation({ sceneId, selectedImageId: imageId });
        toast.success('Image updated');
        closeImageSelectionModal();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update image';
        toast.error(message);
      } finally {
        dispatch({ type: 'SAVE_COMPLETE', payload: sceneId });
      }
    },
    [updateSelectedImageMutation, closeImageSelectionModal]
  );

  // Update scene prompt template
  const updateSceneTemplate = useCallback(
    async (sceneId: Id<'propertyScenes'>, templateId: PromptTemplateId | null) => {
      dispatch({ type: 'START_SAVE', payload: sceneId });
      try {
        await updateSceneTemplateMutation({ sceneId, promptTemplateId: templateId });
        toast.success('Template updated');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update template';
        toast.error(message);
      } finally {
        dispatch({ type: 'SAVE_COMPLETE', payload: sceneId });
      }
    },
    [updateSceneTemplateMutation]
  );

  // Update scene negative prompt
  const updateSceneNegativePrompt = useCallback(
    async (sceneId: Id<'propertyScenes'>, negativePrompt: string) => {
      dispatch({ type: 'START_SAVE', payload: sceneId });
      try {
        await updateSceneNegativePromptMutation({ sceneId, negativePrompt });
        toast.success('Negative prompt updated');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update negative prompt';
        toast.error(message);
      } finally {
        dispatch({ type: 'SAVE_COMPLETE', payload: sceneId });
      }
    },
    [updateSceneNegativePromptMutation]
  );

  const reorderScenes = useCallback(
    async (sceneIds: Id<'propertyScenes'>[]) => {
      if (!videoId) return;
      try {
        await reorderScenesMutation({ videoId, sceneOrder: sceneIds });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reorder';
        toast.error(message);
      }
    },
    [videoId, reorderScenesMutation]
  );

  // Default voice ID if not provided (Rachel - professional female voice)
  const effectiveVoiceId = voiceId ?? '21m00Tcm4TlvDq8ikWAM';

  // Plan scenes - called automatically when entering Step 5 with no scenes
  const planScenes = useCallback(
    async (propertyId: Id<'propertyListings'>, targetDuration: number, aspectRatio: '16:9' | '9:16') => {
      if (!videoId) {
        toast.error('No video selected');
        return;
      }

      dispatch({ type: 'START_PLANNING' });
      dispatch({ type: 'SET_TRIGGERED_PLANNING' });

      try {
        const result = await planVideoScenesAction({
          videoId,
          propertyId,
          targetDuration,
          voiceId: effectiveVoiceId,
          aspectRatio,
        });

        if (result.success) {
          // Only show toast if we actually created scenes (not when another call is handling it)
          if (!result.alreadyInProgress && result.sceneCount > 0) {
            toast.success(`Created ${result.sceneCount} scenes`);
          }
          // If alreadyInProgress, silently complete - the other call will show the toast
          dispatch({ type: 'PLANNING_COMPLETE' });
        } else {
          toast.error(result.error ?? 'Failed to plan scenes');
          dispatch({ type: 'PLANNING_ERROR', payload: result.error ?? 'Unknown error' });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to plan scenes';
        toast.error(message);
        dispatch({ type: 'PLANNING_ERROR', payload: message });
      }
    },
    [videoId, planVideoScenesAction, effectiveVoiceId]
  );

  // Regenerate voiceover for a single scene
  const regenerateVoiceover = useCallback(
    async (sceneId: Id<'propertyScenes'>) => {
      dispatch({ type: 'START_REGENERATE_VOICEOVER', payload: sceneId });
      try {
        const result = await regenerateVoiceoverAction({
          sceneId,
          voiceId: effectiveVoiceId,
        });

        if (!result.success) {
          toast.error(result.error ?? 'Failed to regenerate voiceover');
        } else {
          toast.success('Voiceover regenerated successfully');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to regenerate voiceover';
        toast.error(message);
      } finally {
        dispatch({ type: 'REGENERATE_VOICEOVER_COMPLETE', payload: sceneId });
      }
    },
    [regenerateVoiceoverAction, effectiveVoiceId]
  );

  // Regenerate video for a single scene
  const regenerateVideo = useCallback(
    async (sceneId: Id<'propertyScenes'>) => {
      dispatch({ type: 'START_REGENERATE_VIDEO', payload: sceneId });
      try {
        const result = await regenerateVideoAction({ sceneId });

        if (!result.success) {
          toast.error(result.error ?? 'Failed to start video generation');
        } else {
          toast.success('Video generation started');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to regenerate video';
        toast.error(message);
      } finally {
        dispatch({ type: 'REGENERATE_VIDEO_COMPLETE', payload: sceneId });
      }
    },
    [regenerateVideoAction]
  );

  // Delete a single scene
  const deleteScene = useCallback(
    async (sceneId: Id<'propertyScenes'>) => {
      dispatch({ type: 'START_DELETE', payload: sceneId });
      try {
        await deleteSceneMutation({ sceneId });
        toast.success('Scene deleted successfully');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete scene';
        toast.error(message);
      } finally {
        dispatch({ type: 'DELETE_COMPLETE', payload: sceneId });
      }
    },
    [deleteSceneMutation]
  );

  // Generate all voiceovers for scenes that need them
  const generateAllVoiceovers = useCallback(async () => {
    if (!videoId) {
      toast.error('No video selected');
      return;
    }

    // Filter scenes that will be processed (matching backend logic)
    // Backend processes: scenes without voiceoverAudioId OR with editedVoiceoverText
    const scenesToProcess = (scenes || []).filter(
      (scene) => !scene.voiceoverAudioId || scene.editedVoiceoverText
    );

    // Add all scene IDs to regenerating state before calling backend
    if (scenesToProcess.length > 0) {
      dispatch({
        type: 'START_BULK_REGENERATE_VOICEOVERS',
        payload: scenesToProcess.map((s) => s._id),
      });
    }

    dispatch({ type: 'START_BULK_VOICEOVER' });

    try {
      const result = await generateAllVoiceoversAction({
        videoId,
        voiceId: effectiveVoiceId,
      });

      if (result.success) {
        toast.success(`Generated ${result.completed} voiceovers`);
        dispatch({
          type: 'BULK_VOICEOVER_PROGRESS',
          payload: {
            status: 'completed',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      } else {
        const failedMessage =
          result.failed > 0 ? ` (${result.failed} failed)` : '';
        toast.error(`Voiceover generation incomplete${failedMessage}`);
        dispatch({
          type: 'BULK_VOICEOVER_PROGRESS',
          payload: {
            status: result.completed > 0 ? 'partial' : 'failed',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate voiceovers';
      toast.error(message);
      dispatch({
        type: 'BULK_VOICEOVER_PROGRESS',
        payload: { ...initialBulkProgress, status: 'failed' },
      });
    } finally {
      // Clear the bulk regenerating state after operation completes
      dispatch({ type: 'CLEAR_BULK_REGENERATING_VOICEOVERS' });
    }
  }, [videoId, scenes, generateAllVoiceoversAction, effectiveVoiceId]);

  // Regenerate ALL voiceovers (even ones that already exist)
  const regenerateAllVoiceovers = useCallback(async () => {
    if (!videoId) {
      toast.error('No video selected');
      return;
    }

    // Add all scene IDs to regenerating state
    if (scenes && scenes.length > 0) {
      dispatch({
        type: 'START_BULK_REGENERATE_VOICEOVERS',
        payload: scenes.map((s) => s._id),
      });
    }

    dispatch({ type: 'START_BULK_VOICEOVER' });

    try {
      const result = await regenerateAllVoiceoversAction({
        videoId,
        voiceId: effectiveVoiceId,
      });

      if (result.success) {
        toast.success(`Regenerated ${result.completed} voiceovers`);
        dispatch({
          type: 'BULK_VOICEOVER_PROGRESS',
          payload: {
            status: 'completed',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      } else {
        const failedMessage =
          result.failed > 0 ? ` (${result.failed} failed)` : '';
        toast.error(`Voiceover regeneration incomplete${failedMessage}`);
        dispatch({
          type: 'BULK_VOICEOVER_PROGRESS',
          payload: {
            status: result.completed > 0 ? 'partial' : 'failed',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to regenerate voiceovers';
      toast.error(message);
      dispatch({
        type: 'BULK_VOICEOVER_PROGRESS',
        payload: { ...initialBulkProgress, status: 'failed' },
      });
    } finally {
      dispatch({ type: 'CLEAR_BULK_REGENERATING_VOICEOVERS' });
    }
  }, [videoId, scenes, regenerateAllVoiceoversAction, effectiveVoiceId]);

  // Generate all videos for scenes that have voiceovers ready
  const generateAllVideos = useCallback(async () => {
    if (!videoId) {
      toast.error('No video selected');
      return;
    }

    // Filter scenes that will be processed (matching backend logic)
    // Backend processes: scenes with voiceoverAudioId AND (sourceImageId OR selectedImageId)
    // AND (no generatedVideoId OR imageToVideoStatus === 'failed')
    const scenesToProcess = (scenes || []).filter(
      (scene) =>
        scene.voiceoverAudioId &&
        (scene.sourceImageId || scene.selectedImageId) &&
        (!scene.generatedVideoId || scene.imageToVideoStatus === 'failed')
    );

    // Add all scene IDs to regenerating state before calling backend
    if (scenesToProcess.length > 0) {
      dispatch({
        type: 'START_BULK_REGENERATE_VIDEOS',
        payload: scenesToProcess.map((s) => s._id),
      });
    }

    dispatch({ type: 'START_BULK_VIDEO' });

    try {
      const result = await generateAllVideosAction({ videoId });

      if (result.success) {
        toast.success(`Started generation for ${result.total} videos`);
        dispatch({
          type: 'BULK_VIDEO_PROGRESS',
          payload: {
            status: 'generating',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      } else {
        const failedMessage =
          result.failed > 0 ? ` (${result.failed} failed)` : '';
        toast.error(`Failed to start video generation${failedMessage}`);
        dispatch({
          type: 'BULK_VIDEO_PROGRESS',
          payload: {
            status: result.completed > 0 ? 'partial' : 'failed',
            total: result.total,
            completed: result.completed,
            failed: result.failed,
          },
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to generate videos';
      toast.error(message);
      dispatch({
        type: 'BULK_VIDEO_PROGRESS',
        payload: { ...initialBulkProgress, status: 'failed' },
      });
      // Only clear on error - success case lets scene status updates handle UI
      dispatch({ type: 'CLEAR_BULK_REGENERATING_VIDEOS' });
    }
    // Note: Don't clear regeneratingVideoIds on success - the action now returns immediately
    // (fire-and-forget pattern). Scene status updates via reactive queries will show progress.
    // The regeneratingVideoIds will be recreated on next bulk generation call.
  }, [videoId, scenes, generateAllVideosAction]);

  // Computed values
  const allVoiceoversReady = useMemo(() => {
    if (!scenes || scenes.length === 0) return false;
    return scenes.every((scene) => scene.voiceoverAudioId);
  }, [scenes]);

  const allVideosReady = useMemo(() => {
    if (!scenes || scenes.length === 0) return false;
    return scenes.every((scene) => scene.generatedVideoId);
  }, [scenes]);

  // Computed video progress based on actual scene data
  // This ensures progress reflects reality even when using individual "Generate" buttons
  const computedVideoProgress: IBulkProgress = useMemo(() => {
    if (!scenes || scenes.length === 0) {
      return initialBulkProgress;
    }

    const total = scenes.length;
    const completed = scenes.filter(
      (scene) => scene.generatedVideoId && scene.imageToVideoStatus === 'completed'
    ).length;
    const failed = scenes.filter(
      (scene) => scene.imageToVideoStatus === 'failed'
    ).length;
    const generating = scenes.filter(
      (scene) => scene.imageToVideoStatus === 'generating' || scene.imageToVideoStatus === 'pending'
    ).length;

    // Determine status based on counts
    let status: IBulkProgress['status'] = 'idle';
    if (generating > 0) {
      status = 'generating';
    } else if (completed === total && total > 0) {
      status = 'completed';
    } else if (failed > 0 && completed > 0) {
      status = 'partial';
    } else if (failed > 0 && completed === 0) {
      status = 'failed';
    }

    return { status, total, completed, failed };
  }, [scenes]);

  // Use computed progress when not actively bulk generating, otherwise use state progress
  // This ensures individual "Generate" button clicks update the progress display
  const effectiveVideoProgress: IBulkProgress = useMemo(() => {
    // During bulk generation, prefer state progress for real-time updates
    if (state.videoProgress.status === 'generating') {
      return state.videoProgress;
    }
    // Otherwise, always use computed progress from actual scene data
    return computedVideoProgress;
  }, [state.videoProgress, computedVideoProgress]);

  // Find scenes for modals
  const imageSelectionScene = useMemo(() => {
    if (!state.imageSelectionSceneId || !scenes) return null;
    return scenes.find((s) => s._id === state.imageSelectionSceneId) || null;
  }, [state.imageSelectionSceneId, scenes]);

  const promptModalScene = useMemo(() => {
    if (!state.promptModalSceneId || !scenes) return null;
    return scenes.find((s) => s._id === state.promptModalSceneId) || null;
  }, [state.promptModalSceneId, scenes]);

  return {
    // Data
    scenes: scenes || [],
    isLoading: scenes === undefined,
    error: state.error,

    // View state
    layout: state.layout,
    setLayout,

    // Modal state
    imageSelectionModalOpen: state.imageSelectionModalOpen,
    imageSelectionScene,
    promptModalOpen: state.promptModalOpen,
    promptModalScene,
    openImageSelectionModal,
    closeImageSelectionModal,
    openPromptModal,
    closePromptModal,

    // Scene planning
    planScenes,
    isPlanningScenes: state.isPlanningScenes,
    hasTriggeredPlanning: state.hasTriggeredPlanning,

    // Individual scene actions
    updateVoiceoverText,
    updateImagePrompt,
    updateSelectedImage,
    updateSceneTemplate,
    updateSceneNegativePrompt,
    regenerateVoiceover,
    regenerateVideo,
    deleteScene,

    // Bulk actions
    generateAllVoiceovers,
    regenerateAllVoiceovers,
    generateAllVideos,
    reorderScenes,

    // Progress tracking
    voiceoverProgress: state.voiceoverProgress,
    videoProgress: effectiveVideoProgress,

    // Per-scene loading states
    savingSceneIds: state.savingSceneIds,
    regeneratingVoiceoverIds: state.regeneratingVoiceoverIds,
    regeneratingVideoIds: state.regeneratingVideoIds,
    deletingSceneIds: state.deletingSceneIds,

    // Computed values
    allVoiceoversReady,
    allVideosReady,
  };
}
